import { assign } from 'xstate5';

import { getActiveModificationsCount } from '../../helpers';
import {
  type CustomizationMachineAction,
  type EncouragedChangeEntry,
  type ExtendedModifierGroup,
} from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

export const generateEncouragedChangesList: CustomizationMachineAction = assign((args) => {
  const { context } = args;

  const { modificationGroups, modifications } = context;

  const activeModifications = modifications?.active;
  const shouldSkip = !activeModifications || !modificationGroups;

  if (shouldSkip) {
    return { encouragedChangesList: [] };
  }

  const modificationsCount = getActiveModificationsCount({ activeModifications }); // prettier-ignore
  const createEncouragementChangeEntry = createEncouragementChangeEntryFactory(modificationsCount); // prettier-ignore

  const encouragedChangesList = modificationGroups
    .map(createEncouragementChangeEntry)
    .filter(checkIfValidEntry);

  return { encouragedChangesList };
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

function createEncouragementChangeEntryFactory(modificationsCount: Record<string, number>) {
  return (group: ExtendedModifierGroup): EncouragedChangeEntry | null => {
    const currentSelection = modificationsCount[group.name] ?? 0;

    const shouldEncourageAboveMinSelection = checkIfShouldEncourageAboveMinSelection(group, currentSelection); // prettier-ignore
    const shouldEncourageMaxSelection = checkIfShouldEncourageMaxSelection(group, currentSelection); // prettier-ignore

    // ─────────────────────────────────────────────────────────────────────

    if (shouldEncourageAboveMinSelection) {
      return createAboveMinSelectionEntry(group, currentSelection);
    }

    if (shouldEncourageMaxSelection) {
      return createMaxSelectionEntry(group, currentSelection);
    }

    // ─────────────────────────────────────────────────────────────────────

    return null;
  };
}

function checkIfShouldEncourageAboveMinSelection(
  group: ExtendedModifierGroup,
  currentSelection: number,
): boolean {
  const { encourageAboveMinSelection, minAggregateQuantity } = group;

  if (Number.isNaN(currentSelection)) {
    return false;
  }

  return encourageAboveMinSelection && currentSelection <= minAggregateQuantity;
}

function checkIfShouldEncourageMaxSelection(
  group: ExtendedModifierGroup,
  currentSelection: number,
): boolean {
  const { encourageMaxSelection, maxAggregateQuantity } = group;

  if (Number.isNaN(currentSelection)) {
    return false;
  }

  return encourageMaxSelection && currentSelection < maxAggregateQuantity;
}

function createAboveMinSelectionEntry(
  group: ExtendedModifierGroup,
  currentSelection: number,
): EncouragedChangeEntry {
  return {
    groupDisplayName: group.displayName,
    currentSelection,
    encouragedMinSelection: group.minAggregateQuantity,
    encouragedMaxSelection: group.maxAggregateQuantity,
    modificationIngredientKind: group.modificationIngredientKind,
    encouragementVariation: 'above-min-selection',
  };
}

function createMaxSelectionEntry(
  group: ExtendedModifierGroup,
  currentSelection: number,
): EncouragedChangeEntry {
  return {
    groupDisplayName: group.displayName,
    currentSelection,
    encouragedMinSelection: group.minAggregateQuantity,
    encouragedMaxSelection: group.maxAggregateQuantity,
    modificationIngredientKind: group.modificationIngredientKind,
    encouragementVariation: 'max-selection',
  };
}

function checkIfValidEntry<T>(item: T | null): item is T {
  return item !== null;
}
